// =====================================================================
// commande-prete/portail.js — Commandes prêtes v2 (16-10-2026) — PAGE CLIENT
// =====================================================================
// Page PUBLIQUE /client/<jeton> (ClientPortail.jsx, SPEC-v2 §3 « Page client
// publique » et §5) : le client voit ses commandes (90 derniers jours), ce qui
// est encore à quai et ce qui est parti. Bilingue : anglais + français (bouton).
//   { client: { name }, commandes: [{ ref, preteLe, unites, palettes?, colis?, etat: QUAI|PARTIELLE|CHARGEE,
//     chargeeLe, resteAQuai, detail: [{ nom, nomFr?, sscc, dims, poids, charge, chargeLe? }], photos }] }
//   (dims : texte « 120 × 80 × 145 » en cm, ou { l, w, h } ; poids : kg ; charge : booléen ou date)
// Fonctions PURES (aucun DOM) : testées par frontend/test/commande-prete.test.mjs.
// =====================================================================

import { dateHeureBxlLangue } from './dates.js';

export const FILTRES = ['toutes', 'quai', 'chargees'];

export const TEXTES = {
  en: {
    titre: 'Orders ready for collection',
    sousTitre: 'Last 90 days · most recent first',
    filtres: { toutes: 'All orders', quai: 'Still at our dock', chargees: 'Loaded' },
    colRef: 'Loading reference', colPrete: 'Ready on', colUnites: 'Units', colEtat: 'Status', colChargee: 'Loaded on', colReste: 'Still at our dock',
    etats: { QUAI: 'At our dock', PARTIELLE: 'Partly loaded', CHARGEE: 'Loaded' },
    detail: 'Details', masquer: 'Hide details',
    colNom: 'Unit', colSscc: 'SSCC', colDims: 'Dimensions', colPoids: 'Weight', colCharge: 'Loaded',
    charge: 'Loaded', aQuai: 'At our dock', photos: 'View the photos',
    aucune: 'No order in the last 90 days.', aucuneFiltre: 'No order for this filter.',
    actualiser: 'Refresh', misAJour: 'Updated at', auto: 'automatically every 2 min', autoPause: 'automatic refresh paused: too many requests', chargement: 'Loading…',
    invalide: 'This link has expired or is invalid.', tropDeDemandes: 'Too many requests — please try again in a few minutes.',
    erreur: 'The page could not be loaded. Please try again.', langue: 'Français',
    unite: (n) => `${n} unit${n > 1 ? 's' : ''}`,
    palettes: (n) => `${n} pallet${n > 1 ? 's' : ''}`, colis: (n) => `${n} parcel${n > 1 ? 's' : ''}`,
    noms: { PAL: 'Pallet', COL: 'Parcel' },
    total: 'Total', commandes: (n) => `${n} order${n > 1 ? 's' : ''}`,
  },
  fr: {
    titre: 'Commandes prêtes à l\'enlèvement',
    sousTitre: '90 derniers jours · les plus récentes d\'abord',
    filtres: { toutes: 'Toutes les commandes', quai: 'Encore à quai', chargees: 'Chargées' },
    colRef: 'Réf. chargement', colPrete: 'Prête le', colUnites: 'Unités', colEtat: 'État', colChargee: 'Chargée le', colReste: 'Reste à quai',
    etats: { QUAI: 'À quai', PARTIELLE: 'Partiellement chargée', CHARGEE: 'Chargée' },
    detail: 'Détail', masquer: 'Masquer le détail',
    colNom: 'Unité', colSscc: 'SSCC', colDims: 'Dimensions', colPoids: 'Poids', colCharge: 'Chargée',
    charge: 'Chargée', aQuai: 'À quai', photos: 'Voir les photos',
    aucune: 'Aucune commande ces 90 derniers jours.', aucuneFiltre: 'Aucune commande pour ce filtre.',
    actualiser: 'Actualiser', misAJour: 'Mis à jour à', auto: 'toutes les 2 min, automatiquement', autoPause: 'mise à jour automatique en pause : trop de demandes', chargement: 'Chargement…',
    invalide: 'Ce lien a expiré ou n\'est pas valide.', tropDeDemandes: 'Trop de demandes — réessayez dans quelques minutes.',
    erreur: 'La page n\'a pas pu être chargée. Réessayez.', langue: 'English',
    unite: (n) => `${n} unité${n > 1 ? 's' : ''}`,
    palettes: (n) => `${n} palette${n > 1 ? 's' : ''}`, colis: (n) => `${n} colis`,
    noms: { PAL: 'Palette', COL: 'Colis' },
    total: 'Total', commandes: (n) => `${n} commande${n > 1 ? 's' : ''}`,
  },
};

// Langue de départ : celle du navigateur si c'est le français, sinon l'anglais.
export const langueDepart = (navLangue) => (/^fr\b/i.test(String(navLangue || '')) ? 'fr' : 'en');

const ETATS = ['QUAI', 'PARTIELLE', 'CHARGEE'];
export const etatCommande = (c) => (ETATS.includes(c && c.etat) ? c.etat : 'QUAI');

// « Encore à quai » = tout ou partie à quai ; « Chargées » = tout est parti.
export function filtrerCommandes(commandes, filtre) {
  const l = Array.isArray(commandes) ? commandes : [];
  if (filtre === 'quai') return l.filter((c) => etatCommande(c) !== 'CHARGEE');
  if (filtre === 'chargees') return l.filter((c) => etatCommande(c) === 'CHARGEE');
  return l;
}
export const compteurs = (commandes) => Object.fromEntries(FILTRES.map((f) => [f, filtrerCommandes(commandes, f).length]));

// Nom d'unité (« Palette 2 » / « Pallet 2 ») dans la langue de la page ; autre nom : tel quel.
export function nomUnite(nom, langue) {
  const m = /^(palette|pallet|colis|parcel)\s+(\d+)$/i.exec(String(nom ?? '').trim());
  if (!m) return String(nom ?? '');
  const type = /^pal/i.test(m[1]) ? 'PAL' : 'COL';
  return `${TEXTES[langue].noms[type]} ${m[2]}`;
}

// Nom d'une unité du détail : le nom français fourni (nomFr) en français, sinon traduit.
export function nomDetail(u, langue) {
  if (langue === 'fr' && u && typeof u.nomFr === 'string' && u.nomFr) return u.nomFr;
  return nomUnite(u && u.nom, langue);
}
// « 2 palettes + 1 colis » quand le serveur donne le détail, sinon le nombre d'unités.
export function texteUnitesCommande(c, langue) {
  const T = TEXTES[langue];
  const p = Number(c && c.palettes), k = Number(c && c.colis);
  if (Number.isFinite(p) && Number.isFinite(k) && p + k > 0) return [p ? T.palettes(p) : null, k ? T.colis(k) : null].filter(Boolean).join(' + ');
  const n = Number(c && c.unites);
  return Number.isFinite(n) ? T.unite(n) : '—';
}

// Nombre d'unités restées à quai (nombre, ou liste d'unités).
export const resteNombre = (r) => (Array.isArray(r) ? r.length : Number.isFinite(Number(r)) ? Number(r) : 0);

/** Total des commandes AFFICHÉES (27-10-2026, demande Silviu) : { commandes, palettes, colis, unites, reste }. */
export function totaux(lignes) {
  const n = (v) => (Number.isFinite(Number(v)) ? Number(v) : 0);
  return (lignes || []).reduce((t, c) => ({
    commandes: t.commandes + 1, palettes: t.palettes + n(c.palettes), colis: t.colis + n(c.colis),
    unites: t.unites + n(c.unites), reste: t.reste + resteNombre(c.resteAQuai),
  }), { commandes: 0, palettes: 0, colis: 0, unites: 0, reste: 0 });
}

const nombre = (n, langue) => Number(n).toLocaleString(langue === 'fr' ? 'fr-BE' : 'en-GB', { maximumFractionDigits: 1 });
// Dimensions : texte (« 120 × 80 × 145 », en cm : l'unité est ajoutée), ou { l, w, h } /
// { lengthCm, widthCm, heightCm } en cm.
export function texteDims(d, langue = 'en') {
  if (d === null || d === undefined || d === '') return '—';
  if (typeof d === 'string' || typeof d === 'number') {
    const t = String(d).trim();
    return /^[\d\s×x.,]+$/i.test(t) && /\d/.test(t) ? `${t} cm` : t;
  }
  const l = d.l ?? d.lengthCm, w = d.w ?? d.widthCm, h = d.h ?? d.heightCm;
  const t = [l, w, h].filter((x) => x !== null && x !== undefined && x !== '');
  return t.length ? `${t.map((x) => nombre(x, langue)).join(' × ')} cm` : '—';
}
// Poids : texte tel quel, nombre = kg.
export function textePoids(p, langue = 'en') {
  if (p === null || p === undefined || p === '') return '—';
  if (typeof p === 'number') return `${nombre(p, langue)} kg`;
  return String(p);
}

// Lien vers les photos : seulement un lien du site (/order/…) ou une adresse web http(s) — jamais
// javascript:, data:… (le serveur donne PUBLIC_BASE_URL/order/<jeton> : https en prod, http en test).
export function lienSur(url) {
  const u = String(url ?? '').trim();
  if (!u) return null;
  // « //hôte » et « /\hôte » sont lus comme une autre adresse par le navigateur : refusés.
  if (/^\/(?![/\\])[^\s]*$/.test(u)) return u;
  if (/^https?:\/\/[^\s/\\]+(?:[/?#][^\s]*)?$/i.test(u)) return u;
  return null;
}

// Date et heure de Bruxelles dans la langue de la page (commande-prete/dates.js).
export const dateHeure = (d, langue) => dateHeureBxlLangue(d, langue);

// Unité chargée ? (`charge` : booléen ou date de chargement) et quand (`chargeLe`, ou `charge` daté).
export const uniteChargee = (u) => !!(u && (u.charge || u.chargeLe));
export const dateChargeUnite = (u) => (u && (u.chargeLe || (typeof u.charge === 'string' ? u.charge : null))) || null;
